import { cloneDeep, get, identity, isEmpty, isNil, keyBy, pick, stubTrue, uniq } from 'lodash';
import memoize from 'memoize-one';
import moment from 'moment/moment';
import { SpelExpressionEvaluator, StandardContext } from 'spel2js';
import { DASHBOARD_RANGE_PICKER_PRESETS as PRESETS } from 'taboola-ultimate-ui';
import {
    DIMENSIONS,
    METRICS,
} from '../../../../scheduled-reports/components/DynamicReportsEditorDnD/DynamicReportsEditorDnDNodeTypes';
import { NEW_DYNAMIC_REPORT_ID } from '../../../../scheduled-reports/config/initialValues/newDynamicReportId';
import { CREATIVE_TYPE } from '../../../config';
import { DynamicReportMediaCellRenderer } from '../components/CellRenderers/DynamicReportMediaCellRenderer';
import { FILTER_TYPE, lockedColumn, numericColumn, STATIC_FILTERS_MAP } from '../config';
import EMPTY_FIELD_VALUE from '../config/defaultValues';
import { FILTER_OPERATOR } from '../config/filters/filterOperator';
import { REPORT_PRESET } from '../config/pickers';
import { emptyComparator } from '../utils/emptyComparator';
import generateColumnId from '../utils/generateColumnId';
import { createFooterMapper } from '../utils/normalizeColumnDefs';

const AUTO_FIT_TOLERANCE = 5;
export const INIT_COLUMN_WIDTH = 100;

const ROW_ID_PROPERTY_NAME = 'ROW_ID';
export const TIME_UNITS_GROUP_NAME = 'TIME_UNITS';
const ACCOUNT_ID_LEAF_NAME = 'ACCOUNT_ID';
const numericDataTypes = ['MONEY', 'NUMERIC', 'PERCENT'];

const toApiDatePreset = {
    YESTERDAY: 'YESTERDAY',
    SEVEN_DAYS: 'LAST_7_DAYS',
    FOURTEEN_DAYS: 'LAST_14_DAYS',
    THIRTY_DAYS: 'LAST_30_DAYS',
    NINETY_DAYS: 'LAST_90_DAYS',
    LAST_MONTH: 'LAST_MONTH',
    LAST_QUARTER: 'LAST_QUARTER',
    THIS_MONTH: 'THIS_MONTH',
    THIS_QUARTER: 'THIS_QUARTER',
    THIS_YEAR: 'THIS_YEAR',
    LAST_TWELVE_MONTHS: 'LAST_12_MONTHS',
};
export const DYNAMIC_REPORT_FLOW = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    FETCH: 'FETCH',
};
export const METAMODEL_NODE_TYPE = {
    ROW: 'ROW',
    COLUMN: 'COLUMN',
    GROUP: 'GROUP',
};
export const METAMODEL_FILTER_TYPE = {
    VALUE_FILTER: 'VALUE_FILTER',
    PREDEFINED_FILTER: 'PREDEFINED_FILTER',
};
export const METAMODEL_FILTER_DATA_TYPE = {
    STRING: 'STRING',
    NUMERIC: 'NUMERIC',
    DATE: 'DATE',
};
export const FILTER_REFERENCE_TYPE = {
    'PERFORMANCE_REPORT-CAMPAIGNS_GROUP-CAMPAIGNS_GROUP_ID': FILTER_TYPE.CAMPAIGNS_GROUP_ID_VIEW,
    'PERFORMANCE_REPORT-ACCOUNT-ACCOUNT_ID': FILTER_TYPE.ACCOUNT_NAME,
    'PERFORMANCE_REPORT-CAMPAIGN-CAMPAIGN_ID': FILTER_TYPE.CAMPAIGN_ID,
    'PERFORMANCE_REPORT-TARGETING-SITE-ID': FILTER_TYPE.SITE_ID_VIEW,
    'PERFORMANCE_REPORT-TARGETING-PLATFORM-CODE': FILTER_TYPE.PLATFORM,
};

const getFilterLabel = (formatMessage, referencedFilterType, defaultLabel) => {
    return formatMessage({
        id: `app.campaigns.reports.filters.type.${referencedFilterType}`,
        defaultMessage: defaultLabel,
    });
};

export const defaultDateRange = {
    nodeReference: { name: 'PERFORMANCE_REPORT-TIME_UNITS-DAY' },
    filterType: METAMODEL_FILTER_TYPE.PREDEFINED_FILTER,
    filterOperator: FILTER_OPERATOR.EQUALS,
    values: [toApiDatePreset[PRESETS.SEVEN_DAYS]],
};

export const getColIdByDynamicReport = (dynamicReport, col) =>
    generateColumnId(dynamicReport?.id, get(col, 'nodeReference.name'));

const isMediaContent = contentTypeValue => {
    return contentTypeValue === CREATIVE_TYPE.IMAGE || contentTypeValue === CREATIVE_TYPE.VIDEO;
};

const getCellRenderer = col => {
    if (!col?.details?.values) return;

    const contentType = col.details.values.find(detail => detail.key === 'content_type');

    if (contentType && isMediaContent(contentType?.value)) {
        return DynamicReportMediaCellRenderer;
    }
};

export const getColumnDefsByDynamicReport = (dynamicReport, metamodel) => {
    const nodeValues = metamodel?.report?.nodes?.values;
    const dimensions = transformMetaModelNodesToList(nodeValues, METAMODEL_NODE_TYPE.ROW);
    const dimensionsMap = keyBy(dimensions, 'id');
    const metrics = transformMetaModelNodesToList(nodeValues, METAMODEL_NODE_TYPE.COLUMN);
    const metricsMap = keyBy(metrics, 'id');
    const columnDefs = get(dynamicReport, 'columns.values', []).map(col => {
        const nodeName = get(col, 'nodeReference.name');
        const nodeLabel = get(col, 'nodeReference.label');
        const headerName =
            nodeLabel || dimensionsMap[nodeName]?.label || metricsMap[nodeName]?.label || 'Missed column header';

        return {
            headerName,
            field: nodeName,
            colId: getColIdByDynamicReport(dynamicReport, col),
            width: col.width || INIT_COLUMN_WIDTH,
            comparator: emptyComparator,
            sortIndex: col.sortIndex,
            sort: col?.sortOrder && `${col.sortOrder}`.toLowerCase(),
            ...(dimensionsMap[nodeName]
                ? { type: getColumnTypeByDataType(get(dimensionsMap[nodeName], 'dataType')) }
                : {
                      type: getColumnTypeByDataType(get(metricsMap[nodeName], 'dataType')),
                  }),
            cellRendererParams: get(dimensionsMap[nodeName], 'details'),
            cellRenderer: getCellRenderer(dimensionsMap[nodeName]),
            valueFormatter: ({ value }) => value || EMPTY_FIELD_VALUE,
        };
    });

    return columnDefs;
};

const getColumnTypeByDataType = dataType => {
    const baseColumns = [lockedColumn];

    if (numericDataTypes.includes(dataType)) {
        baseColumns.push(numericColumn);
    }

    return baseColumns;
};

export const getColumStateByDynamicReport = memoize(dynamicReport => {
    const columnState = getColumnDefsByDynamicReport(dynamicReport).map(col =>
        pick(col, ['colId', 'width', 'sort', 'sortIndex'])
    );

    return columnState;
});

export const alignColumnsToColumnState = (dynamicReport, columnState) => {
    const newDynamicReport = cloneDeep(dynamicReport);
    const indexMap = keyBy(
        columnState.map((item, index) => ({ ...item, index })),
        'colId'
    );

    newDynamicReport.columns.values.sort(
        (a, b) =>
            get(indexMap, `${getColIdByDynamicReport(newDynamicReport, a)}.index`) -
            get(indexMap, `${getColIdByDynamicReport(newDynamicReport, b)}.index`)
    );
    newDynamicReport.columns.values = newDynamicReport.columns.values.map(col => ({
        ...col,
        width: get(indexMap, `${getColIdByDynamicReport(newDynamicReport, col)}.width`),
        sortIndex: get(indexMap, `${getColIdByDynamicReport(newDynamicReport, col)}.sortIndex`),
        sortOrder:
            get(indexMap, `${getColIdByDynamicReport(newDynamicReport, col)}.sort`) &&
            `${get(indexMap, `${getColIdByDynamicReport(newDynamicReport, col)}.sort`)}`.toUpperCase(),
    }));

    return newDynamicReport;
};

const buildFilterValueFormatter = () => identity;

const parseMetamodelFilters = (nodeValues = [], filters = [], formatMessage) => {
    const parsedFilters = nodeValues.reduce((acc, { type, nodes, ...node }) => {
        if (type === METAMODEL_NODE_TYPE.GROUP) {
            return [...acc, ...parseMetamodelFilters(nodes?.values, [], formatMessage)];
        }

        const filterList = get(node, 'filters.values', [])
            .map((nodeFilter, index) => {
                const referencedFilterType =
                    nodeFilter.type === METAMODEL_FILTER_TYPE.VALUE_FILTER ? FILTER_REFERENCE_TYPE[node.name] : null;
                if (!referencedFilterType) {
                    return null;
                }
                const enableCondition = stubTrue;
                const options =
                    nodeFilter.type === METAMODEL_FILTER_TYPE.PREDEFINED_FILTER
                        ? get(nodeFilter, 'formattingOptions.values', []).map(({ formattingPattern }) => ({
                              id: formattingPattern,
                              label: formattingPattern,
                          }))
                        : null;
                const isListOnly = nodeFilter.operators.includes(FILTER_OPERATOR.IN);
                const id = node.name + (node.filters.values.length === 1 ? '' : index);

                const filter = {
                    id,
                    label: getFilterLabel(formatMessage, referencedFilterType, node.label),
                    dataType: node.dataType,
                    ...(isListOnly ? {} : { operators: nodeFilter.operators }),
                    formatter: buildFilterValueFormatter(),
                    referencedFilterType,
                    multiSelect: isListOnly,
                    disabled: !enableCondition(),
                    enableCondition,
                    searchable: true,
                    ...(options ? { options } : {}),
                };

                return filter;
            })
            .filter(filter => !isNil(filter));

        return [...acc, ...filterList];
    }, filters);

    return parsedFilters;
};

export const getDynamicReportFilters = (metaModel = {}, _, formatMessage) => {
    const nodeValues = metaModel?.report?.nodes?.values || [];

    const filters = parseMetamodelFilters(nodeValues, [], formatMessage);

    return filters;
};

export const transformDynamicReportToConfig = (dynamicReport, metaModel, formatMessage) => {
    const columnsDef = getColumnDefsByDynamicReport(dynamicReport, metaModel);
    const presets = {
        [REPORT_PRESET.DEFAULT]: {
            columns: columnsDef.map(({ colId }) => colId),
        },
    };
    const filters = getDynamicReportFilters(metaModel, dynamicReport, formatMessage);
    const footerColumnDef = getFooterColumnDefs(dynamicReport, metaModel);

    return {
        id: dynamicReport?.id,
        endpoint: accountId => `/${accountId}/dynamic-reports/data`,
        columnsDef,
        footerColumnDef,
        presets,
        rowIdField: ROW_ID_PROPERTY_NAME,
        customRestrictedFilters: {},
        filters,
        postBody: transformDynamicReportToGw(dynamicReport, DYNAMIC_REPORT_FLOW.FETCH),
    };
};

const getFooterColumnDefs = (dynamicReport, metamodel) => {
    const nodeValues = metamodel?.report?.nodes?.values;
    const rows = transformMetaModelNodesToList(nodeValues, METAMODEL_NODE_TYPE.ROW);
    const rowsFields = rows.map(row => ({
        field: get(row, 'id'),
    }));
    const footerColumnDefs = rowsFields.map(createFooterMapper(dynamicReport.id));

    return footerColumnDefs;
};

export const getDynamicReportWithoutWidth = dynamicReport => {
    const newDynamicReport = cloneDeep(dynamicReport);

    newDynamicReport.columns.values = newDynamicReport.columns.values.map(({ width: _width, ...rest }) => rest);

    return newDynamicReport;
};

export const isAutoFit = columnState => {
    const columnStateWidthOnly = uniq(columnState.map(({ width }) => width));
    const isAutoWidthChange = columnStateWidthOnly.every(
        width => Math.abs(width - get(columnStateWidthOnly, 0, 0)) < AUTO_FIT_TOLERANCE
    );

    return isAutoWidthChange;
};

export const getEmptyDynamicReport = (formatMessage, reportRefType, accountId, initialDateRange) => ({
    id: NEW_DYNAMIC_REPORT_ID,
    name: formatMessage({ id: 'dynamic.reports.dynamic.report.new.report.label' }),
    columns: { values: [] },
    filters: { values: [] },
    reportRef: { reportType: reportRefType },
    numericAccountId: accountId,
    dateRange: initialDateRange,
});

export const transformMetaModelNodesToList = (nodes = [], type, excludeNodeList = []) => {
    const result = nodes.flatMap(node =>
        node.type === type && !excludeNodeList.includes(node.name)
            ? {
                  ...node,
                  id: node.name,
                  name: node.name,
                  restrictions: node.restrictions,
                  type: node.type,
                  label: node.label,
              }
            : transformMetaModelNodesToList(node.nodes?.values || [], type, excludeNodeList)
    );

    return result;
};

export const transformReportColumnToUI = column => {
    return {
        ...column,
        id: column.nodeReference.name,
        name: column.nodeReference.name,
        label: column.nodeReference.label,
    };
};

export const transformUIColumnToReport = column => {
    return { nodeReference: { name: column.name, label: column.label } };
};

export const isDnDForbiddenByType = (droppableId, dragSourceId) => {
    const isForbiddenType = type =>
        droppableId.toUpperCase().includes(type) && !dragSourceId.toUpperCase().includes(type);

    return isForbiddenType(DIMENSIONS) || isForbiddenType(METRICS);
};

export const transformDynamicReportToGw = (dynamicReport, flow) => {
    const { id: _id, reportRef, ...dataPost } = dynamicReport;

    const newDynamicReport = {
        ...dataPost,
        numericAccountId: dataPost.numericAccountId,
        filters: { values: transformSelectedFiltersToFilterValues(dataPost, flow) },
    };

    if (DYNAMIC_REPORT_FLOW.UPDATE !== flow) {
        newDynamicReport.reportRef = reportRef;
    }

    return newDynamicReport;
};

export const transformSelectedFiltersToFilterValues = (dataPost, flow) => {
    const { selectedFilters = [], dateRange = defaultDateRange } = dataPost;
    const accountFilter = accountIdToAccountIdFilter(dataPost.numericAccountId);
    const setSelectedFiltersToEmpty = flow === DYNAMIC_REPORT_FLOW.FETCH && !dataPost.filterActive;
    const transformedFilters = setSelectedFiltersToEmpty
        ? []
        : selectedFilters
              .filter(
                  filter => !filter.type.includes(TIME_UNITS_GROUP_NAME) && !filter.type.includes(ACCOUNT_ID_LEAF_NAME)
              )
              .map(({ type, values }) => ({
                  nodeReference: { name: type },
                  filterOperator: FILTER_OPERATOR.IN,
                  filterType: METAMODEL_FILTER_TYPE.VALUE_FILTER,
                  values: values.map(({ value }) => value.toString()),
              }));

    return [...transformedFilters, dateRange, accountFilter];
};

const accountIdToAccountIdFilter = accountId => {
    return {
        nodeReference: { name: 'PERFORMANCE_REPORT-ACCOUNT-ACCOUNT_ID' },
        filterType: METAMODEL_FILTER_TYPE.PREDEFINED_FILTER,
        filterOperator: FILTER_OPERATOR.EQUALS,
        values: [accountId],
    };
};

const mapFilterValueToUIValue = (filterName, filterValue) => {
    if (
        filterName === 'PERFORMANCE_REPORT-CAMPAIGN-CAMPAIGN_ID' ||
        filterName === 'PERFORMANCE_REPORT-TARGETING-SITE-ID'
    ) {
        return parseInt(filterValue);
    }
    return filterValue;
};

const mapFilterValueToUILabel = (filterName, filterValue, formatMessage) => {
    const filterType = FILTER_REFERENCE_TYPE[filterName];
    if (filterType) {
        const staticFilter = STATIC_FILTERS_MAP[FILTER_REFERENCE_TYPE[filterName]];
        if (staticFilter) {
            const { messageIdPrefix } = staticFilter;

            return formatMessage({ id: `${messageIdPrefix}.${filterValue}`, defaultMessage: filterValue });
        }
    }
    return filterValue;
};

export const transformDynamicReportFromGw = (dynamicReport, { formatMessage }) => {
    const reportsFilters = dynamicReport.filters?.values || [];
    const selectedFilters = reportsFilters
        .filter(
            filter =>
                !filter.nodeReference.name.includes(TIME_UNITS_GROUP_NAME) &&
                !filter.nodeReference.name.includes(ACCOUNT_ID_LEAF_NAME)
        )
        .map(({ nodeReference, values, filterType }) => ({
            type: nodeReference.name,
            label: getFilterLabel(formatMessage, FILTER_REFERENCE_TYPE[nodeReference.name], nodeReference.label),
            values: values.map(value => ({
                value: mapFilterValueToUIValue(nodeReference.name, value),
                label: mapFilterValueToUILabel(nodeReference.name, value, formatMessage),
            })),
            filterType,
            filterOperator: FILTER_OPERATOR.IN,
        }));

    const dateRange = getDateRangeFromGw(reportsFilters);

    return { ...dynamicReport, selectedFilters, dateRange, filterActive: !isEmpty(selectedFilters) };
};

const getDateRangeFromGw = reportsFilters => {
    const dateRange = reportsFilters.find(filter => filter.nodeReference.name.includes(TIME_UNITS_GROUP_NAME));
    return dateRange;
};

export const createDateRange = (startDate, endDate, preset) => {
    if (PRESETS.CUSTOM === preset) {
        return {
            nodeReference: { name: 'PERFORMANCE_REPORT-TIME_UNITS-DAY' },
            filterType: METAMODEL_FILTER_TYPE.VALUE_FILTER,
            filterOperator: FILTER_OPERATOR.BETWEEN,
            values: [moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD')],
        };
    }
    return {
        nodeReference: { name: 'PERFORMANCE_REPORT-TIME_UNITS-DAY' },
        filterType: METAMODEL_FILTER_TYPE.PREDEFINED_FILTER,
        filterOperator: FILTER_OPERATOR.EQUALS,
        values: [toApiDatePreset[preset]],
    };
};

const matchGroupLimits = (req, groupName, limit) => {
    const targetingColumns = req.columns.values;
    const targetingGroups = targetingColumns
        .filter(col => col.name.startsWith(groupName.replaceAll('.', '-')))
        .map(col => {
            const fullName = col.name;
            return fullName.substring(0, fullName.lastIndexOf('-'));
        });

    return uniq(targetingGroups).length <= limit;
};

const spelContext = StandardContext.create({}, {});

export const canSelectColumn = (request, column) => {
    if (!request?.columns?.values || isEmpty(column)) return false;

    const clonedRequest = {
        ...cloneDeep(request),
        columns: {
            ...request.columns,
            values: [...request.columns.values, column],
        },
    };

    const disabled = (column?.selectableConditions?.values || []).some(condition => {
        const compiled = SpelExpressionEvaluator.compile(condition.value);
        return !compiled.eval(spelContext, {
            request: clonedRequest,
            unique: uniq,
            matchGroupLimits,
        });
    });

    return disabled;
};
